import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Image as ImageIcon, Link2, Video, Lock, Users, Globe2, X, Trash2 } from "lucide-react";

function fileToDataURL(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function ytId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.split("/").filter(Boolean)[0] || null;
    if (u.hostname.includes("youtube.com")) {
      if (u.searchParams.get("v")) return u.searchParams.get("v");
      const parts = u.pathname.split("/").filter(Boolean);
      const i = parts.findIndex((p) => p === "embed" || p === "shorts");
      if (i >= 0 && parts[i + 1]) return parts[i + 1];
    }
  } catch { return null; }
  return null;
}

const VIS = {
  public: { label: "Public", icon: Globe2 },
  connections: { label: "Connections", icon: Users },
  private: { label: "Private", icon: Lock },
};

export default function Feed() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState({
    body: "", image_data_url: "", video_url: "", link_url: "", visibility: "public",
  });
  const [posting, setPosting] = useState(false);

  const loadFeed = useCallback(() => {
    setLoading(true);
    api.get("/posts/feed")
      .then((r) => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
    if (!f.type.startsWith("image/")) { toast.error("Images only."); return; }
    try {
      setComposer({ ...composer, image_data_url: await fileToDataURL(f) });
    } finally {
      e.target.value = "";
    }
  };

  const publish = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login"); return; }
    if (!composer.body.trim() && !composer.image_data_url && !composer.video_url && !composer.link_url) {
      toast.error("Add something to post."); return;
    }
    setPosting(true);
    try {
      await api.post("/posts", {
        body: composer.body.trim() || null,
        image_data_url: composer.image_data_url || null,
        video_url: composer.video_url.trim() || null,
        link_url: composer.link_url.trim() || null,
        visibility: composer.visibility,
      });
      toast.success("Posted.");
      setComposer({ body: "", image_data_url: "", video_url: "", link_url: "", visibility: "public" });
      loadFeed();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setPosting(false);
    }
  };

  const deletePost = async (post_id) => {
    if (!confirm("Delete this post?")) return;
    try {
      await api.delete(`/posts/${post_id}`);
      toast.success("Deleted.");
      loadFeed();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const canPost = !!user;

  return (
    <div className="max-w-[820px] mx-auto px-5 lg:px-10 py-10" data-testid="feed-page">
      <div className="label text-pfc-gold mb-2">PFC · Community Feed</div>
      <h1 className="font-display text-3xl sm:text-4xl text-white uppercase leading-tight mb-8">
        The <span className="gold-text">Feed</span>
      </h1>

      {/* COMPOSER */}
      {user && (
        <form onSubmit={publish} className="pfc-card p-5 mb-8" data-testid="feed-composer">
          <textarea
            value={composer.body}
            onChange={(e) => setComposer({ ...composer, body: e.target.value })}
            placeholder="What's happening?"
            rows={3} maxLength={2000}
            className="w-full bg-ink-900 border border-white/10 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none resize-none"
            data-testid="feed-composer-body"
          />

          {composer.image_data_url && (
            <div className="relative inline-block mt-3">
              <img src={composer.image_data_url} alt="" className="max-h-44 rounded border border-pfc-gold/40" />
              <button type="button" onClick={() => setComposer({ ...composer, image_data_url: "" })} className="absolute -top-2 -right-2 bg-pfc-red text-white w-6 h-6 rounded-full text-xs font-bold">×</button>
            </div>
          )}
          {(composer.video_url || composer.link_url) && (
            <div className="mt-3 space-y-2">
              {composer.video_url && (
                <div className="flex items-center gap-2 text-xs text-white/60 font-body">
                  <Video size={14} className="text-pfc-gold" /> {composer.video_url}
                  <button type="button" onClick={() => setComposer({ ...composer, video_url: "" })} className="ml-auto text-pfc-red hover:text-red-300"><X size={14} /></button>
                </div>
              )}
              {composer.link_url && (
                <div className="flex items-center gap-2 text-xs text-white/60 font-body">
                  <Link2 size={14} className="text-pfc-gold" /> {composer.link_url}
                  <button type="button" onClick={() => setComposer({ ...composer, link_url: "" })} className="ml-auto text-pfc-red hover:text-red-300"><X size={14} /></button>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <label className="cursor-pointer p-2 text-white/60 hover:text-pfc-gold transition" data-testid="feed-add-image">
              <ImageIcon size={18} />
              <input type="file" accept="image/*" hidden onChange={onPickImage} />
            </label>
            <button
              type="button"
              onClick={() => {
                const u = prompt("Video URL (YouTube, Vimeo, direct)");
                if (u) setComposer({ ...composer, video_url: u });
              }}
              className="p-2 text-white/60 hover:text-pfc-gold transition" data-testid="feed-add-video"
            >
              <Video size={18} />
            </button>
            <button
              type="button"
              onClick={() => {
                const u = prompt("Link URL");
                if (u) setComposer({ ...composer, link_url: u });
              }}
              className="p-2 text-white/60 hover:text-pfc-gold transition" data-testid="feed-add-link"
            >
              <Link2 size={18} />
            </button>
            <select
              value={composer.visibility}
              onChange={(e) => setComposer({ ...composer, visibility: e.target.value })}
              className="bg-ink-900 border border-white/15 text-white px-3 py-2 font-sub uppercase tracking-[0.15em] text-[11px] focus:border-pfc-gold focus:outline-none"
              data-testid="feed-visibility"
            >
              <option value="public">🌐 Public</option>
              <option value="connections">👥 Connections</option>
              <option value="private">🔒 Private (only you)</option>
            </select>
            <button
              type="submit" disabled={posting}
              className="ml-auto font-sub uppercase tracking-[0.25em] text-xs px-5 py-2.5 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50"
              data-testid="feed-publish"
            >
              {posting ? "Posting…" : "Post"}
            </button>
          </div>
        </form>
      )}

      {/* FEED */}
      {loading ? (
        <div className="text-white/40 text-center py-16 font-sub uppercase tracking-[0.2em]">Loading feed…</div>
      ) : posts.length === 0 ? (
        <div className="text-white/40 text-center py-16 font-sub uppercase tracking-[0.2em]" data-testid="feed-empty">
          No posts yet · be the first.
        </div>
      ) : (
        <div className="space-y-5" data-testid="feed-list">
          {posts.map((p) => {
            const VisIcon = VIS[p.visibility]?.icon || Globe2;
            const yt = p.video_url ? ytId(p.video_url) : null;
            const mine = user && p.author_id === user.user_id;
            return (
              <article key={p.post_id} className="pfc-card p-5" data-testid={`post-${p.post_id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-pfc-gold/20 border border-pfc-gold/40 flex items-center justify-center font-display text-pfc-gold" style={{ borderRadius: 5 }}>
                    {(p.author?.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    {p.author?.profile_id ? (
                      <Link to={`/profile/${p.author.profile_id}`} className="font-display text-white text-base hover:text-pfc-gold transition">
                        {p.author?.name || "Member"}
                      </Link>
                    ) : (
                      <span className="font-display text-white text-base">{p.author?.name || "Member"}</span>
                    )}
                    <p className="text-white/45 text-[11px] font-sub uppercase tracking-wider mt-0.5 flex items-center gap-2">
                      {new Date(p.created_at).toLocaleString()}
                      <span className="inline-flex items-center gap-1 text-white/40"><VisIcon size={11} /> {VIS[p.visibility]?.label}</span>
                    </p>
                  </div>
                  {mine && (
                    <button onClick={() => deletePost(p.post_id)} className="text-white/40 hover:text-pfc-red transition p-1" data-testid={`delete-${p.post_id}`} title="Delete">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                {p.body && <p className="text-white/85 font-body whitespace-pre-wrap break-words">{p.body}</p>}
                {p.image_data_url && (
                  <img src={p.image_data_url} alt="" className="mt-3 w-full max-h-[500px] object-contain bg-ink-900" style={{ borderRadius: 8 }} />
                )}
                {yt && (
                  <div className="mt-3 relative w-full overflow-hidden" style={{ paddingTop: "56.25%", borderRadius: 8 }}>
                    <iframe
                      title={p.post_id}
                      src={`https://www.youtube.com/embed/${yt}?rel=0&modestbranding=1`}
                      className="absolute inset-0 w-full h-full"
                      frameBorder="0" allowFullScreen loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    />
                  </div>
                )}
                {!yt && p.video_url && (
                  <a href={p.video_url} target="_blank" rel="noopener noreferrer" className="block mt-3 px-4 py-3 border border-white/10 hover:border-pfc-gold text-pfc-gold font-sub uppercase tracking-[0.2em] text-xs">
                    ▶ Watch video
                  </a>
                )}
                {p.link_url && (
                  <a href={p.link_url} target="_blank" rel="noopener noreferrer" className="block mt-3 px-4 py-3 border border-white/10 hover:border-pfc-gold text-white/80 font-body text-sm truncate">
                    🔗 {p.link_url}
                  </a>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
